/**
 * WindowManagerRegistry - Registre des Window Managers
 *
 * Permet à des applications comme DeskWeb de s'enregistrer comme
 * gestionnaire de fenêtres actif. Quand un WM est enregistré, le kernel
 * lui délègue la création des fenêtres pour les apps GUI.
 *
 * Si aucun WM n'est enregistré, le SystemWindowManager est utilisé
 * comme fallback.
 *
 * Le kernel garde le contrôle du positionnement des iframes pour éviter
 * les problèmes de cross-origin avec les blob URLs. Le WM envoie les
 * bounds de ses fenêtres et le kernel positionne les iframes en overlay.
 */

import { getProcessManager } from './process-manager.js';

let instance = null;

export class WindowManagerRegistry {
  constructor() {
    if (instance) {
      return instance;
    }

    /**
     * WM actuellement actif
     * @type {null | {pid: number, port: MessagePort, capabilities: string[]}}
     */
    this.activeWM = null;

    /**
     * Bounds des fenêtres gérées par le WM externe (pid → bounds)
     * @type {Map<number, {x: number, y: number, width: number, height: number, visible: boolean, zIndex: number}>}
     */
    this.windowBounds = new Map();

    /**
     * Callback pour les événements WM
     * @type {Map<string, Function[]>}
     */
    this.listeners = new Map();

    instance = this;
  }

  /**
   * Enregistre un Window Manager
   * @param {number} pid - PID du processus WM
   * @param {MessagePort} port - Port pour communiquer avec le WM
   * @param {string[]} capabilities - Capacités du WM (ex: ['windows', 'taskbar'])
   * @returns {{success: boolean, error?: string}}
   */
  register(pid, port, capabilities = ['windows']) {
    // Un seul WM peut être actif à la fois
    if (this.activeWM !== null) {
      console.warn(`[WMRegistry] WM already registered (pid ${this.activeWM.pid}), replacing with pid ${pid}`);
      this.unregister(this.activeWM.pid);
    }

    this.activeWM = {
      pid,
      port,
      capabilities,
      registeredAt: Date.now()
    };

    console.log(`[WMRegistry] Registered WM pid=${pid} with capabilities:`, capabilities);
    this.emit('registered', { pid, capabilities });

    return { success: true };
  }

  /**
   * Désenregistre un Window Manager
   * @param {number} pid - PID du processus WM à désenregistrer
   * @returns {{success: boolean, error?: string}}
   */
  unregister(pid) {
    if (!this.activeWM) {
      return { success: false, error: 'No WM registered' };
    }

    if (this.activeWM.pid !== pid) {
      return { success: false, error: `WM with pid ${pid} is not the active WM` };
    }

    const oldWM = this.activeWM;
    this.activeWM = null;

    // Nettoyer les bounds des fenêtres
    this.clearAllBounds();

    console.log(`[WMRegistry] Unregistered WM pid=${pid}`);
    this.emit('unregistered', { pid: oldWM.pid });

    return { success: true };
  }

  /**
   * Vérifie si un WM est actuellement enregistré
   * @returns {boolean}
   */
  hasActiveWM() {
    return this.activeWM !== null;
  }

  /**
   * Récupère le port du WM actif
   * @returns {MessagePort | null}
   */
  getActiveWMPort() {
    return this.activeWM?.port ?? null;
  }

  /**
   * Récupère les informations du WM actif
   * @returns {null | {pid: number, capabilities: string[], registeredAt: number}}
   */
  getActiveWMInfo() {
    if (!this.activeWM) return null;

    return {
      pid: this.activeWM.pid,
      capabilities: this.activeWM.capabilities,
      registeredAt: this.activeWM.registeredAt
    };
  }

  /**
   * Vérifie si le WM actif a une capacité spécifique
   * @param {string} capability - Capacité à vérifier
   * @returns {boolean}
   */
  hasCapability(capability) {
    if (!this.activeWM) return false;
    return this.activeWM.capabilities.includes(capability);
  }

  /**
   * Envoie un message au WM actif
   * @param {string} type - Type de message
   * @param {object} data - Données du message
   * @returns {boolean} - true si le message a été envoyé
   */
  sendToWM(type, data) {
    if (!this.activeWM || !this.activeWM.port) {
      return false;
    }

    try {
      this.activeWM.port.postMessage({ type, data });
      return true;
    } catch (error) {
      console.error('[WMRegistry] Failed to send message to WM:', error);
      return false;
    }
  }

  /**
   * Demande au WM de créer une fenêtre
   * @param {object} options - Options de la fenêtre
   * @param {number} options.pid - PID du processus
   * @param {string} options.title - Titre de la fenêtre
   * @param {string} options.icon - Chemin de l'icône
   * @param {number} options.width - Largeur
   * @param {number} options.height - Hauteur
   * @param {number} options.minWidth - Largeur minimum
   * @param {number} options.minHeight - Hauteur minimum
   * @returns {boolean} - true si la demande a été envoyée
   */
  requestCreateWindow(options) {
    return this.sendToWM('REQ_WM_CREATE_WINDOW', options);
  }

  // ═══════════════════════════════════════════════════════════════
  // GESTION DES BOUNDS ET POSITIONNEMENT DES IFRAMES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Met à jour les bounds d'une fenêtre et repositionne l'iframe
   * @param {number} pid - PID du processus
   * @param {object} bounds - Nouvelles bounds
   * @param {number} bounds.x - Position X
   * @param {number} bounds.y - Position Y
   * @param {number} bounds.width - Largeur
   * @param {number} bounds.height - Hauteur
   * @param {boolean} bounds.visible - Si la fenêtre est visible
   * @param {number} bounds.zIndex - Z-index de la fenêtre
   */
  updateWindowBounds(pid, bounds) {
    console.log(`[WMRegistry] updateWindowBounds PID=${pid}`, bounds);
    this.windowBounds.set(pid, bounds);
    this.positionIframe(pid);
  }

  /**
   * Positionne l'iframe d'un processus selon ses bounds
   * @param {number} pid - PID du processus
   */
  positionIframe(pid) {
    const bounds = this.windowBounds.get(pid);
    if (!bounds) {
      console.warn(`[WMRegistry] positionIframe: No bounds for PID=${pid}`);
      return;
    }

    const processManager = getProcessManager();
    const process = processManager.getProcess(pid);
    if (!process) {
      console.warn(`[WMRegistry] positionIframe: No process for PID=${pid}`);
      return;
    }
    if (!process.iframe) {
      console.warn(`[WMRegistry] positionIframe: No iframe for PID=${pid} (process exists: ${process.name})`);
      return;
    }

    const iframe = process.iframe;

    if (!bounds.visible) {
      console.log(`[WMRegistry] positionIframe: Hiding iframe for PID=${pid}`);
      iframe.style.display = 'none';
      return;
    }

    // Positionner l'iframe en overlay
    console.log(`[WMRegistry] positionIframe: Positioning iframe for PID=${pid} at (${bounds.x}, ${bounds.y}) ${bounds.width}x${bounds.height} z=${bounds.zIndex}`);
    iframe.style.display = 'block';
    iframe.style.position = 'fixed';
    iframe.style.left = `${bounds.x}px`;
    iframe.style.top = `${bounds.y}px`;
    iframe.style.width = `${bounds.width}px`;
    iframe.style.height = `${bounds.height}px`;
    iframe.style.zIndex = String(bounds.zIndex || 100);
    iframe.style.border = 'none';

    // Debug: vérifier que l'iframe est dans le DOM
    console.log(`[WMRegistry] positionIframe: iframe in DOM: ${!!iframe.parentNode}, display: ${iframe.style.display}`);
  }

  /**
   * Supprime les bounds d'une fenêtre
   * @param {number} pid - PID du processus
   */
  removeWindowBounds(pid) {
    this.windowBounds.delete(pid);
  }

  /**
   * Nettoie les bounds lors du désenregistrement du WM
   */
  clearAllBounds() {
    // Cacher toutes les iframes gérées avant de nettoyer
    const processManager = getProcessManager();
    for (const [pid] of this.windowBounds) {
      const process = processManager.getProcess(pid);
      if (process?.iframe) {
        process.iframe.style.display = 'none';
      }
    }
    this.windowBounds.clear();
  }

  /**
   * Ajoute un listener pour un événement
   * @param {string} event - Nom de l'événement ('registered', 'unregistered')
   * @param {Function} callback - Fonction à appeler
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Supprime un listener
   * @param {string} event - Nom de l'événement
   * @param {Function} callback - Fonction à supprimer
   */
  off(event, callback) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Émet un événement
   * @param {string} event - Nom de l'événement
   * @param {object} data - Données de l'événement
   */
  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      for (const callback of callbacks) {
        try {
          callback(data);
        } catch (error) {
          console.error(`[WMRegistry] Error in ${event} listener:`, error);
        }
      }
    }
  }
}

/**
 * Récupère l'instance singleton du WindowManagerRegistry
 * @returns {WindowManagerRegistry}
 */
export function getWMRegistry() {
  if (!instance) {
    instance = new WindowManagerRegistry();
  }
  return instance;
}

export default WindowManagerRegistry;
